import { useCallback, useState } from 'react'

import type { CommandBarAction, RecentItem } from '../actions/types'

const STORAGE_KEY = 'buffer-command-bar-recent'
const MAX_RECENT_ITEMS = 5

/**
 * Type guard to validate a RecentItem shape at runtime
 */
function isValidRecentItem(item: unknown): item is RecentItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'actionId' in item &&
    typeof item.actionId === 'string' &&
    'timestamp' in item &&
    typeof item.timestamp === 'number'
  )
}

/**
 * Load recent items from localStorage
 */
function loadRecentItems(): RecentItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (Array.isArray(parsed) && parsed.every(isValidRecentItem)) {
        return parsed
      }
      // Data shape mismatch - clear corrupted data
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Ignore parse errors
  }
  return []
}

/**
 * Save recent items to localStorage
 */
function saveRecentItems(items: RecentItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Return value from the useRecentItems hook.
 */
interface UseRecentItemsResult {
  /** Add an action to recent items */
  addRecentItem: (actionId: string) => void
  /** Get recent actions from the provided actions list */
  getRecentActions: (actions: CommandBarAction[]) => CommandBarAction[]
  /** Clear all recent items */
  clearRecentItems: () => void
}

/**
 * Hook for managing recent items in the command bar.
 *
 * Recent items are shown at the top of the command bar for quick access
 * to frequently used actions.
 */
export function useRecentItems(): UseRecentItemsResult {
  const [recentItems, setRecentItems] = useState<RecentItem[]>(() =>
    loadRecentItems(),
  )

  const addRecentItem = useCallback((actionId: string) => {
    setRecentItems((prev) => {
      // Remove existing entry for this action (we'll add it to the front)
      const filtered = prev.filter((item) => item.actionId !== actionId)

      // Add to front with current timestamp
      const updated: RecentItem[] = [
        { actionId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_ITEMS)

      saveRecentItems(updated)
      return updated
    })
  }, [])

  const getRecentActions = useCallback(
    (actions: CommandBarAction[]): CommandBarAction[] => {
      if (recentItems.length === 0) {
        return []
      }

      // Create a map for quick lookup (excluding actions that should not appear in recent)
      const actionMap = new Map(
        actions.filter((a) => !a.excludeFromFrecency).map((a) => [a.id, a]),
      )

      // Get actions in recent order
      const recentActions: CommandBarAction[] = []
      for (const item of recentItems) {
        const action = actionMap.get(item.actionId)
        if (action) {
          // Clone the action and update its section to 'recent'
          // Prefix ID to distinguish from the original action in the list
          recentActions.push({
            ...action,
            id: `recent-${action.id}`,
            section: 'recent',
            displayName: action.displayName,
          })
        }
      }

      return recentActions
    },
    [recentItems],
  )

  const clearRecentItems = useCallback(() => {
    setRecentItems([])
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore errors
    }
  }, [])

  return {
    addRecentItem,
    getRecentActions,
    clearRecentItems,
  }
}
